// دیالوگ‌های تأیید و دلیل الزامی (IA §4.4: عملیات برگشت‌ناپذیر با ReasonDialog).
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import { useTranslation } from '@phoenix/i18n';
import { useEffect, useId, useState, type ReactNode } from 'react';
import { faDigits } from '../format/digits';
import { ErrorState } from './states';

export interface ConfirmDialogProps {
  open: boolean;
  titleKey: string;
  messageKey?: string;
  messageValues?: Record<string, unknown>;
  confirmKey?: string;
  /** اقدام مخرب (دکمه قرمز). */
  destructive?: boolean;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  /** خطای اقدام (ApiError) درون دیالوگ. */
  error?: unknown;
}

export function ConfirmDialog({
  open,
  titleKey,
  messageKey,
  messageValues,
  confirmKey,
  destructive = false,
  busy = false,
  onConfirm,
  onCancel,
  error,
}: ConfirmDialogProps) {
  const { t } = useTranslation('common');
  const titleId = useId();
  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onCancel}
      aria-labelledby={titleId}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle id={titleId}>{t(titleKey)}</DialogTitle>
      {(messageKey || Boolean(error)) && (
        <DialogContent>
          {messageKey && <DialogContentText>{t(messageKey, messageValues)}</DialogContentText>}
          {error ? <ErrorState error={error} compact /> : null}
        </DialogContent>
      )}
      <DialogActions>
        <Button onClick={onCancel} disabled={busy}>
          {t('common:actions.cancel')}
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          color={destructive ? 'error' : 'primary'}
          disabled={busy}
          autoFocus
          data-testid="confirm-dialog-confirm"
        >
          {t(confirmKey ?? 'common:actions.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export interface ReasonDialogProps {
  open: boolean;
  titleKey: string;
  messageKey?: string;
  confirmKey?: string;
  /** حداقل طول دلیل (پیش‌فرض ۳). */
  minLength?: number;
  maxLength?: number;
  busy?: boolean;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
  /** فیلدهای اضافه پیش از دلیل (مثل حالت هدف تعلیق). */
  children?: ReactNode;
  /** خطای ثبت (ApiError) درون دیالوگ. */
  error?: unknown;
  /** اقدام مخرب (دکمه قرمز؛ پیش‌فرض true). */
  destructive?: boolean;
}

/** دیالوگ «دلیل الزامی» برای لغو، ابطال، Override و تعلیق. */
export function ReasonDialog({
  open,
  titleKey,
  messageKey,
  confirmKey,
  minLength = 3,
  maxLength = 500,
  busy = false,
  onConfirm,
  onCancel,
  children,
  error,
  destructive = true,
}: ReasonDialogProps) {
  const { t } = useTranslation('common');
  const titleId = useId();
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (open) {
      setReason('');
      setTouched(false);
    }
  }, [open]);

  const trimmed = reason.trim();
  const invalid = trimmed.length < minLength;

  const submit = () => {
    setTouched(true);
    if (!invalid) {
      onConfirm(trimmed);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onCancel}
      aria-labelledby={titleId}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle id={titleId}>{t(titleKey)}</DialogTitle>
      <DialogContent>
        {messageKey && <DialogContentText sx={{ mb: 2 }}>{t(messageKey)}</DialogContentText>}
        {children}
        <TextField
          autoFocus
          required
          multiline
          minRows={3}
          label={t('common:dialogs.reason_label')}
          value={reason}
          onChange={(event) => setReason(event.target.value.slice(0, maxLength))}
          onBlur={() => setTouched(true)}
          error={touched && invalid}
          helperText={touched && invalid ? t('common:dialogs.reason_min', { min: faDigits(minLength) }) : ' '}
          slotProps={{ htmlInput: { 'data-testid': 'reason-input', maxLength } }}
        />
        {error ? <ErrorState error={error} compact /> : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={busy}>
          {t('common:actions.cancel')}
        </Button>
        <Button
          onClick={submit}
          variant="contained"
          color={destructive ? 'error' : 'primary'}
          disabled={busy}
          data-testid="reason-dialog-confirm"
        >
          {t(confirmKey ?? 'common:actions.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
